'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LuCalendarDays, LuDot } from 'react-icons/lu';

import type { Course } from '@eliadmin/core/src/api/courses/coursers-api';
import type { AddToCartButtonProps } from '@eliadmin/core/src/components/product/add-to-cart-button';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { CourseLevel } from '../../courses/course-level';
import { translateCustomDate } from '../event-card';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

interface EventCourseCardProps {
  course: Course;
  externalRegistration?: string;
  hidePrice?: boolean;
}

const COURSE_CUSTOM_DATE_FIELD_ID = 7;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const CourseDetailAddToCartButton = ({ product, ...props }: AddToCartButtonProps) => (
  <VariantsProvider
    key={`product-${product.id ?? product.slug}`}
    state={{ product: product as any }}
  >
    <DialogProvider>
      <AddToCartButton {...props} product={product} />
    </DialogProvider>
  </VariantsProvider>
);

const EventCourseCardInternal = ({
  course,
  externalRegistration,
  hidePrice = false,
}: EventCourseCardProps) => {
  const me = useCustomerStore(s => s.me);
  const { t } = useTranslations();
  const { data: cart } = useQuery({ ...useCartQueryOpts(), enabled: !!me?.authToken });
  const [isItemInCart, setIsItemInCart] = useState(false);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const priceDisplay = course.product ? getProductPriceDisplay(course.product) : null;

  // Vlastný text (napr. „Pondelok a streda“) má prednosť pred dátumom z date selectora.
  const customDate = course.text_fields
    ?.find(field => field.id === COURSE_CUSTOM_DATE_FIELD_ID)
    ?.value?.trim();
  // Viac termínov v jednom texte sa oddeľuje znakom „|“ – každý termín sa zobrazí ako samostatný blok.
  const dateSlots = customDate
    ? translateCustomDate(customDate, t)
        .split('|')
        .map(slot => slot.trim())
        .filter(Boolean)
    : course.date_start
      ? [formatDate(course.date_start)]
      : [];

  useEffect(() => {
    if (cart && course?.product && course.product.id) {
      const cartItem = cart.items?.find(item => item.product.id === course.product!.id);
      setIsItemInCart(!!cartItem);
    }
  }, [cart, course.product?.id]);

  return (
    <div className="flex min-h-[115px] w-full flex-col items-start justify-start gap-4 border border-[#E5E5E5] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col items-start justify-start gap-1.5 sm:gap-0">
        <div className="flex w-full items-start justify-between gap-2 sm:gap-4">
          {course?.levels && course?.levels.length > 0 && <CourseLevel course={course} />}
          {course?.time_start && course?.time_stop && (
            <p className="text-sm font-normal text-black sm:text-base">
              {course?.time_start}&nbsp;-&nbsp;{course?.time_stop}
            </p>
          )}
        </div>
        {dateSlots.length > 0 && (
          <div className="my-1.5 flex w-full items-start justify-start gap-2">
            <LuCalendarDays size={18} className="mt-0.5 shrink-0 text-primary" />
            <div className="flex flex-wrap items-center justify-start gap-x-4 gap-y-0.5">
              {dateSlots.map((slot, index) => (
                <p
                  key={`${slot}-${index}`}
                  className="whitespace-nowrap text-sm font-medium text-black sm:text-base"
                >
                  {slot}
                </p>
              ))}
            </div>
          </div>
        )}
        <h4 className="text-sm font-bold text-black sm:text-base">{course.name}</h4>
        <div className="flex flex-wrap items-center justify-start gap-x-3 gap-y-1.5 sm:gap-y-1">
          {course.lecturers?.map(lecturer => (
            <Link
              key={lecturer.id}
              prefetch={false}
              href={lecturer.slug}
              className="flex items-center justify-start gap-2"
            >
              <Image
                src={lecturer.image?.url ?? '/images/user.png'}
                alt={resolveImageAlt(lecturer.image, lecturer.name || 'Lektor')}
                width={32}
                height={32}
                className="h-8 w-8 rounded-full bg-white object-cover object-center p-[2px]"
              />
              <p className="text-sm font-normal text-black/70">{lecturer.name || 'Lektor'}</p>
            </Link>
          ))}
          {course.room && (
            <>
              <LuDot size={20} className="hidden text-black/70 sm:block" />
              <p className="text-sm font-normal text-black/70">{course.room.name}</p>
            </>
          )}
          {!!(course.max_capacity && course.product?.quantity) && (
            <>
              <LuDot size={20} className="hidden text-black/70 sm:block " />
              <p className="text-sm font-normal text-black/70">
                {t('text2200')} {course.max_capacity - course.product.quantity}/
                {course.max_capacity}
              </p>
            </>
          )}
        </div>
      </div>
      <div className="flex w-full items-center justify-start gap-8 sm:w-fit sm:justify-end sm:gap-4 md:gap-6 lg:gap-8">
        {!hidePrice && (
          <strong className="whitespace-nowrap text-lg font-bold text-black sm:text-base">
            {priceDisplay?.priceOnRequest
              ? t('text2278')
              : formatPrice(
                  priceDisplay?.currentWithVat,
                  generalLayout.currency,
                  generalLayout.currency_settings,
                )}
          </strong>
        )}
        {externalRegistration ? (
          <Link
            prefetch={false}
            href={externalRegistration}
            target="_blank"
            className="flex h-12 w-fit items-center justify-center bg-primary px-6 font-bold text-white"
          >
            {t('text1340')}
          </Link>
        ) : (
          course.product &&
          !priceDisplay?.priceOnRequest && (
            <CourseDetailAddToCartButton
              product={course.product}
              quantity={1}
              sellable={!isItemInCart && !course.is_purchased}
              customText={t('text1403')}
              className="w-fit whitespace-nowrap text-white"
            />
          )
        )}
      </div>
    </div>
  );
};

export const EventCourseCard = ({
  course,
  externalRegistration,
  hidePrice = false,
}: EventCourseCardProps) => (
  <VariantsProvider key={`course-${course.id ?? course.slug}`} state={{ product: course as any }}>
    <DialogProvider>
      <EventCourseCardInternal
        course={course}
        externalRegistration={externalRegistration}
        hidePrice={hidePrice}
      />
    </DialogProvider>
  </VariantsProvider>
);
